import React, { useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import { Grid, Tooltip, IconButton, TableCell } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApi, doSwaggerCall } from '../../hooks/useApi';
import useNotify from '../../hooks/useNotify';
import { TABLE_ROW_COUNT_DEFAULT } from '../../helpers/constants';
import PageWithTitle from '../../components/atom/PageWithTitle';
import GenericTable from '../../components/atom/GenericTable';
import { USERS_TABLE_COLUMNS } from '../../helpers/tableColumnLists';
import SearchInput from '../../components/atom/SearchInput';
import { MODAL_TYPE, useModals } from '../../hooks/useModal';
import { addUser, deleteUser, updateUser, replaceDashes } from '../../helpers/handleImportUser';
import ModalContentSkeleton from './components/ModalContentSkeleton';
import { importedDataToRawData } from '../../helpers/handleImportedObjects';
import handleNavigation from '../../helpers/handleNavigation';
import ExportImportUsersButtons from './components/ExportImportUsersButtons';

const UsersPage = () => {
    const { notifyError, notifySuccess } = useNotify();
    const [searchStr, setSearchStr] = useState('');
    const location = useLocation();
    const [{ limit, page, sortorder, sortby }, setParams] = useState({
        limit: TABLE_ROW_COUNT_DEFAULT,
        page: 0,
        sortby: 'lastname',
        sortorder: 'ASC',
    });
    const [data, loading, error, setData, reloadData] = useApi('Users', 'getUsers', {
        limit,
        offset: page * limit,
        search: searchStr,
        sortby,
        sortorder,
    });

    const [rows, setRows] = useState([]);
    const { showModal } = useModals();
    const [modalContent, setModalContent] = useState({
        newItems: [],
        updateItems: [],
        deleteItems: [],
    });
    const navigate = useNavigate();
    const [importedData, setImportedData] = useState(null);

    const handleOnConfirm = () => {
        const { newItems, updateItems, deleteItems } = modalContent;
        if (newItems.length !== 0) {
            // eslint-disable-next-line no-restricted-syntax
            for (const item of newItems) {
                const birthdate = new Date(item.birthdate);

                if (!item.firstname || !item.lastname || !item.email || !item.role) {
                    notifyError(
                        'A keresztnév, vezetéknév, e-mail cím, jogosultság kitöltése kötelező!'
                    );
                } else if (rows.some((row) => row.email.toLowerCase() === item.email.trim())) {
                    notifyError(`${item.email} e-mail cím már létezik!`);
                } else if (item.birthdate && Number.isNaN(birthdate.getMonth())) {
                    notifyError(
                        `A(z) ${item.email} e-mail című felhasználónál adjon meg egy valós születési dátumot, vagy hagyja üresen!`
                    );
                } else {
                    addUser(
                        item,
                        () => {
                            notifySuccess(
                                `${item.email} e-mail című felhasználó sikeresen hozzáadva!`
                            );
                            reloadData();
                        },
                        () => {
                            notifyError(
                                `Hiba történt a ${item.email} e-mail című felhasználó hozzáadása során!`
                            );
                        }
                    );
                }
            }
        }
        if (updateItems.length !== 0) {
            // eslint-disable-next-line no-restricted-syntax
            for (const item of updateItems) {
                updateUser(
                    item.newValues,
                    () => {
                        notifySuccess(
                            `${item.newValues.id} azonosítójú felhasználó sikeresen frissítve!`
                        );
                        reloadData();
                    },
                    () => {
                        notifyError(
                            `Hiba történt a ${item.newValues.id} azonosítójú felhasználó frissítése során!`
                        );
                    }
                );
            }
        }
        if (deleteItems.length !== 0) {
            // eslint-disable-next-line no-restricted-syntax
            for (const item of deleteItems) {
                if (!item.id) {
                    notifyError(`${item.id} azonosítójú felhasználó nem található`);
                }
                deleteUser(
                    item,
                    () => {
                        notifySuccess(`${item.id} azonosítójú felhasználó sikeresen törölve!`);
                        reloadData();
                    },
                    () => {
                        notifyError(
                            `Hiba történt a ${item.id} azonosítójú felhasználó törlése során!`
                        );
                    }
                );
            }
        }
    };

    useEffect(() => {
        if (importedData) {
            const deleteItems = [];
            const updateItems = [];
            const newItems = [];

            importedData.forEach((e) => {
                const item = importedDataToRawData(e);
                const oldItem = replaceDashes(rows.find((row) => row.id === item.id));

                if (item.delete) {
                    deleteItems.push(item);
                } else if (!item.id) {
                    newItems.push(item);
                } else if (oldItem) {
                    // if any of the following cloumns has been changed we want to update them
                    if (
                        oldItem.lastname !== item.lastname ||
                        oldItem.firstname !== item.firstname ||
                        oldItem.lang !== item.lang ||
                        oldItem.role !== item.role ||
                        oldItem.vipurl !== item.vipurl ||
                        (oldItem.lurdylifemember.key === 'member' && !item.lurdylifemember) ||
                        (oldItem.lurdylifemember.key === 'notMember' && item.lurdylifemember)
                    ) {
                        updateItems.push({
                            oldValues: { ...oldItem },
                            newValues: { ...item },
                        });
                    }
                }
            });
            setModalContent({
                deleteItems,
                updateItems,
                newItems,
            });
        }
    }, [importedData]);

    useEffect(() => {
        if (modalContent && importedData) {
            showModal(MODAL_TYPE.CONFIRMATION, {
                title:
                    modalContent.deleteItems?.length !== 0 ||
                    modalContent.updateItems?.length !== 0 ||
                    modalContent.newItems?.length !== 0
                        ? 'Biztosan végerhajtja az alábbi változtatásokat?'
                        : 'Nem történt változás az adatállományban',
                content: <ModalContentSkeleton modalContent={modalContent} />,
                confirm: () => {
                    handleOnConfirm();
                },
                cancel: () => {
                    setModalContent(null);
                    setImportedData(null);
                },
                limit,
                setParams,
                width: 'lg',
            });
        }
    }, [modalContent, importedData]);

    useEffect(() => {
        if (error) {
            notifyError(error);
        }
    }, [error, notifyError]);

    useEffect(() => {
        setParams({
            limit: TABLE_ROW_COUNT_DEFAULT,
            page: 0,
            search: searchStr,
        });
    }, [searchStr]);

    useEffect(() => {
        setRows(
            data?.users.map((user) => {
                return {
                    birthdate: user.birthdate?.replaceAll('-', '. ') || '-',
                    email: user.email,
                    firstname: user.firstname,
                    lang: user.lang,
                    lastname: user.lastname,
                    role: user.role,
                    id: user.id,
                    registration: user.created_at.replaceAll('-', '. '),
                    vipurl: user.vipurl || '-',
                    lurdylifemember: user.lurdylifemember ? (
                        <IconButton key="member">
                            <CheckIcon color="success" />
                        </IconButton>
                    ) : (
                        <IconButton key="notMember">
                            <CloseIcon color="error" />
                        </IconButton>
                    ),
                    editIcon: (
                        <TableCell align="right" sx={{ width: '5%' }}>
                            <Tooltip title="Részletek">
                                <IconButton
                                    onClick={() => {
                                        handleNavigation(location, (stateObj) => {
                                            navigate(`/users/edit/${user.id}`, stateObj);
                                        });
                                    }}
                                >
                                    <EditIcon />
                                </IconButton>
                            </Tooltip>
                        </TableCell>
                    ),
                    deleteIcon: (
                        <TableCell align="right" sx={{ width: '5%' }}>
                            <Tooltip title="Törlés">
                                <IconButton
                                    onClick={() => {
                                        showModal(MODAL_TYPE.CONFIRMATION, {
                                            title: 'Felhasználó törlése',
                                            content: `Biztosan törli a ${user.email} e-mail című felhasználót?`,
                                            confirm: async () => {
                                                await doSwaggerCall('Users', 'deleteUser', {
                                                    id: user.id,
                                                });
                                                reloadData();
                                            },
                                        });
                                    }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </TableCell>
                    ),
                };
            })
        );
    }, [data?.users]);

    return (
        <PageWithTitle
            title="Felhasználók"
            headline={
                <ExportImportUsersButtons
                    total={data?.total}
                    setImportedData={setImportedData}
                    setModalContent={setModalContent}
                />
            }
        >
            <Grid container>
                <Grid item xs={10}>
                    <Formik initialValues={{ search: '' }}>
                        {({ setFieldValue }) => (
                            <Form>
                                <Grid container alignItems="center" sx={{ ml: 1.3 }}>
                                    <Tooltip
                                        title="Keresés vezetéknév, keresztnév, vagy e-mail alapján"
                                        arrow
                                    >
                                        <Grid item>
                                            <SearchInput
                                                sm={12}
                                                name="search"
                                                label="Keresés"
                                                type="text"
                                                onChangeProps={{
                                                    fieldId: 'search',
                                                    setFieldValue,
                                                    setSearchStr,
                                                }}
                                            />
                                        </Grid>
                                    </Tooltip>
                                </Grid>
                            </Form>
                        )}
                    </Formik>
                </Grid>
            </Grid>
            <GenericTable
                rows={rows}
                limit={limit}
                page={page}
                setParams={setParams}
                total={data?.total}
                loading={loading}
                order={sortorder}
                orderBy={sortby}
                columns={USERS_TABLE_COLUMNS}
                navigationLink="/users/edit/"
                detailsIcon={<EditIcon />}
            />
        </PageWithTitle>
    );
};

export default UsersPage;
